import { Divider } from '@design-system';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { csrClient } from '../../../apollo/api/avantArteClient';
import { storefrontClient } from '../../../apollo/shopify/storefrontClient';
import AvantArteAuthGateway from '../../../gateways/AvantArteAuthGateway';
import { AvantArteProductGateway } from '../../../gateways/AvantArteProductGateway';
import { ShopifyCheckoutGateway } from '../../../gateways/ShopifyCheckoutGateway';
import { ShopifyProductsGateway } from '../../../gateways/ShopifyProductsGateway';
import { useProductBasketPurchasing } from '../../../hooks/useProductBasketPurchasing';
import { useReleaseGating } from '../../../hooks/useReleaseGating';
import {
  GeneratedCalculateOrderPricingQuery,
  GeneratedTaxType,
} from '../../../types/generated';
import { Frame } from '../../../types/Products';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { TrackNftPrintServiceForm } from '../../../utils/analytics';
import { framedByDefault } from '../../../utils/frames';
import Drawer from '../../Layout/Drawer';
import { NftTokenOption } from '../../Web3/NftPrintService/ImagePicker/ImagePicker';
import { ImagePickerModule } from '../../Web3/NftPrintService/ImagePicker/ImagePickerModule';
import { OrderReview } from '../../Web3/NftPrintService/OrderReview/OrderReview';
import { WalletDropdown } from '../../Web3/WalletDropDown/WalletDropdown';
import { useDelegateCash } from '../../Web3/Web3Provider/useDelegateCash';
import { useWeb3 } from '../../Web3/Web3Provider/useWeb3';
import { ProductCountdown } from '../ProductDate/ProductCountdown';

const authGateway = new AvantArteAuthGateway(csrClient, storefrontClient);
const checkoutGateway = new ShopifyCheckoutGateway(storefrontClient);
const productsGateway = new ShopifyProductsGateway(storefrontClient);
const aaProductsGateway = new AvantArteProductGateway(csrClient);

type Props<T extends ProductPurchaseDetails> = {
  unlockCode?: string;
  release?: string;
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
};

export const NFTPrintServiceForm: FC<Props<ProductPurchaseDetails>> = ({
  product,
  purchasingController,
  release,
  unlockCode,
}) => {
  const [{ id }] = product.id;
  const { walletAddress } = useWeb3();
  const { gatedRelease } = useReleaseGating({ product });
  const [openReview, setOpenReview] = useState(false);
  const delegateCash = useDelegateCash({
    chainId: gatedRelease?.chainId,
    filterContractAddress: gatedRelease?.nftContract,
    shouldQuery: true,
  });
  const [calculateOrderPricingData, setCalculateOrderPricingData] = useState<
    GeneratedCalculateOrderPricingQuery['calculateOrderPricing'] | undefined
  >();

  const {
    addFrameToProduct,
    addProductToBasket,
    calculateBasketPricing,
    checkoutLoading,
    frameBasket,
    goToCheckout,
    productBasket,
    removeFrameFromProduct,
    removeProductFromBasket,
  } = useProductBasketPurchasing({
    authGateway,
    checkoutGateway,
    productsGateway,
    aaProductsGateway,
    release,
  });

  useEffect(() => {
    calculateBasketPricing().then(setCalculateOrderPricingData);
  }, [calculateBasketPricing, productBasket, frameBasket]);

  const shipping = calculateOrderPricingData?.subtotalShippingPrice || 0;
  const subtotal = calculateOrderPricingData?.subtotalItemsPrice || 0;
  const subTotalAfterTaxAndShipping =
    calculateOrderPricingData?.totalPrice || 0;
  const taxCalculation = {
    taxRate: calculateOrderPricingData?.taxRate || 0,
    taxType: calculateOrderPricingData?.taxType || GeneratedTaxType.NoTax,
    calculatedAmount: calculateOrderPricingData?.subtotalTaxPrice || 0,
  };

  const [selectedFrameLocal, setSelectedFrameLocal] = useState<Frame | null>(
    null,
  );
  const frameIncluded = framedByDefault(
    purchasingController.product?.framingOffered,
  );

  useEffect(() => {
    if (!selectedFrameLocal) setSelectedFrameLocal(purchasingController.frame);
  }, [purchasingController.frame, selectedFrameLocal]);

  const { frameOptions } = purchasingController;

  const artistName = useMemo(() => {
    if (product.productArtist) {
      const [resolved] = product.productArtist;
      if (resolved) {
        return (resolved as ResolvedRelatedArtist).artist?.name || '';
      }
    }
    return '';
  }, [product.productArtist]);

  const handleOpenReviewOrder = useCallback(() => {
    setOpenReview(true);
    TrackNftPrintServiceForm({
      action: 'open order review',
      productTitle: product.title,
      walletAddress,
    });
  }, [product.title, walletAddress]);

  const handleCloseReviewOrder = useCallback(() => {
    setOpenReview(false);
    TrackNftPrintServiceForm({
      action: 'close order review',
      productTitle: product.title,
      walletAddress,
    });
  }, [product.title, walletAddress]);

  const handleLogout = useCallback(() => {
    TrackNftPrintServiceForm({
      action: 'wallet log out',
      productTitle: product.title,
      walletAddress,
    });
  }, [product.title, walletAddress]);

  const handleAddToBasket = useCallback(
    (token: NftTokenOption) => {
      addProductToBasket({
        productId: { id },
        quantity: 1,
        unlockCode,
        customProperties: [
          { key: 'token_id', value: token.id },
          {
            key: '_token_img',
            value: gatedRelease?.printMatchesNft
              ? token.imageUris.nft
              : token.imageUris.print || '',
          },
        ],
      });
      TrackNftPrintServiceForm({
        action: 'add token',
        productTitle: product.title,
        tokenId: token.id,
        walletAddress,
      });
    },
    [addProductToBasket, id],
  );

  const handleRemoveFromBasket = useCallback(
    (token: NftTokenOption) => {
      const found = Object.entries(productBasket).find(
        ([, item]) =>
          !!(item.customProperties || []).find(
            ({ key, value }) => key === 'token_id' && value === token.id,
          ),
      );
      if (found) {
        const [entryId] = found;
        removeProductFromBasket(entryId);
      }
      TrackNftPrintServiceForm({
        action: 'remove token',
        productTitle: product.title,
        tokenId: token.id,
        walletAddress,
      });
    },
    [productBasket, removeProductFromBasket, product.title, walletAddress],
  );

  const handleAddFrame = useCallback(
    (entryId: string) => {
      if (!selectedFrameLocal) return;

      addFrameToProduct(entryId, selectedFrameLocal);
      TrackNftPrintServiceForm({
        action: 'add frame',
        productTitle: product.title,
        frameId: entryId,
        walletAddress,
      });
    },
    [addFrameToProduct, selectedFrameLocal, product.title, walletAddress],
  );

  const handleRemoveFrame = useCallback(
    (entryId: string) => {
      removeFrameFromProduct(entryId);
      TrackNftPrintServiceForm({
        action: 'remove frame',
        productTitle: product.title,
        frameId: entryId,
        walletAddress,
      });
    },
    [removeFrameFromProduct, product.title, walletAddress],
  );

  const handleSelectFrame = (frame: Frame) => {
    if (!frame) {
      setSelectedFrameLocal(null);
      return;
    }

    setSelectedFrameLocal(frame);
  };

  const handleSelectFrameVariant = (selectedVariant: Frame) => {
    if (!selectedFrameLocal) return;

    setSelectedFrameLocal(selectedVariant);
  };

  const handleGoToCheckout = useCallback(() => {
    if (walletAddress) {
      goToCheckout([{ key: '_wallet_address', value: walletAddress }]);
    }
    TrackNftPrintServiceForm({
      action: 'go to checkout',
      productTitle: product.title,
      walletAddress,
    });
  }, [goToCheckout, product.title, walletAddress]);

  return (
    <div className="flex w-full flex-col gap-4">
      <ProductCountdown date={purchasingController.product?.closingDate} />
      <WalletDropdown
        activeDelegate={delegateCash.selectedDelegation}
        delegates={delegateCash.delegations}
        onDelegateSelected={delegateCash.setActiveDelegation}
        onLogout={handleLogout}
        onMainWalletSelected={delegateCash.removeActiveDelegation}
      />
      <Divider />
      <ImagePickerModule
        isLoading={checkoutLoading}
        onAdd={handleAddToBasket}
        onRemove={handleRemoveFromBasket}
        onSubmit={handleOpenReviewOrder}
        orderItems={productBasket}
        product={product}
      />
      <Drawer
        onClose={handleCloseReviewOrder}
        overlayOtherDrawers
        shown={openReview}
      >
        {openReview && (
          <OrderReview
            artistName={artistName}
            collectionTitle={gatedRelease?.nftCollectionTitle || ''}
            frame={selectedFrameLocal}
            frameIncluded={frameIncluded}
            frames={frameOptions}
            loading={checkoutLoading}
            onAddFrame={handleAddFrame}
            onRemoveFrame={handleRemoveFrame}
            onSelectFrame={handleSelectFrame}
            onSelectFrameVariant={handleSelectFrameVariant}
            onSubmit={handleGoToCheckout}
            orderItems={productBasket}
            shipping={shipping}
            storyblokProduct={product}
            subtotal={subtotal}
            tax={taxCalculation.calculatedAmount}
            total={subTotalAfterTaxAndShipping}
          />
        )}
      </Drawer>
    </div>
  );
};
